import React, { useState, useEffect, useContext, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Flex,
  Button,
  Grid,
  GridItem,
  Image,
  Modal,
  ModalHeader,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Th,
  Tr,
  Thead,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { WebAPIContext } from '../../contexts/WebAPIContext';
// import html2canvas from 'html2canvas';
// import jsPDF from 'jspdf';
import logoImage from '../../assets/logo.png';
import { Opportunity } from '../../contexts/types';
// import { YYYYMM_DISPLAY_FORMAT, YYYYMMDD_DISPLAY_FORMAT } from '../../constants/common';
// import { formatDate } from '../../utils/common'
import dayjs from 'dayjs';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  topicsInfo: Opportunity | null;
  from?: string;
}

// 商流(Channel)の表示階位（固定3行）
const CHANNEL_LEVELS = ['2nd', '3rd', '4th'];

// 客先種別（0:未選択, 1:EMS, 2:代理店, 3:その他）。未選択は空欄表示。
const getClientTypeLabels = (t: (key: string) => string): Record<number, string> => ({
  0: '',
  1: 'EMS',
  2: t('features.Opportunity.TopicsModal.label.clientTypeAgency'),
  3: t('features.Opportunity.TopicsModal.label.clientTypeOther'),
});

interface ItemProps {
  itemLabel: string;
  headValue?: string;
  value: string | number | null | undefined;
  minH?: string;
}

type Update = {
  UpdateDate: string;
  CurrentStatus: string;
};

const TopicsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  topicsInfo,
  from
}) => {
  // ↓↓↓↓↓↓ 移植 ↓↓↓↓↓↓
  // mm-sf\src\constants\common.ts
  const YYYYMM_DISPLAY_FORMAT = "YYYY/MM";
  const YYYYMMDD_DISPLAY_FORMAT = "YYYY/MM/DD";

  // mm-sf\src\utils\common.ts (moment⇒dayjsに置き換え)
  /**
   * 日付情報を指定のフォーマットに変換
   */
  function formatDate(date: string | null | undefined, format: string) {
    if (date && dayjs(date).isValid()) {
      return dayjs(date).format(format);
    } else {
      return null;
    }
  }
  // ↑↑↑↑↑↑ 移植 ↑↑↑↑↑↑

  const { t } = useTranslation();
  const WebAPI = useContext(WebAPIContext);
  const toast = useToast();
  const global = useSelector((state: RootState) => state.store);
  const CLIENT_TYPE_LABELS = getClientTypeLabels(t);

  const [customerId, setCustomerId] = useState<string | null>(null);
  const [imageFileName, setImageFileName] = useState<string | null>(null);
  const [uploadedImageFileContent, setUploadedImageFileContent] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] = useState<Update | null>(null);

  useEffect(() => {
    // モーダルが開かれたときだけ項目を初期化する
    if (isOpen) {
      //    ボタンの実装に伴いコメントアウト
      //    トピックス画面を閉じる方法をメッセージ表示
      // toast({
      //   description: t('features.Opportunity.TopicsModal.message.closeWithEsc'),
      //   status: 'info',
      //   duration: 5000,
      //   isClosable: true,
      // });

      //   setCurrentStatus(null);
      setImageFileName(null);
      //   setUploadedImageFileContent(null);
      //   if (from === 'opportunityChart') {
      //     setCustomerId(topicsInfo?.customer?.id ?? null);
      //   } else {
      //     setCustomerId(global.selected.customerId);
      //   }
      //   getCustomerProductInfo();

      setImageFileName(topicsInfo?.customerProduct?.imageFileName ?? null);
      getLatestCurrentStatus(topicsInfo?.currentStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    // 画像ファイルの取得
    if (imageFileName) {
      const fetchData = async () => {
        try {
          const result = await WebAPI.request({
            query: `
          query getCustomerProductFileByCustomerProductId($customerId: String, $customerProductId: String, $imageFileName: String) {
            getCustomerProductFileByCustomerProductId(customerId: $customerId, customerProductId: $customerProductId, imageFileName: $imageFileName) {
              customerProductId
              customerId
              imageFileName
              content
            }
          }
        `,
            variables: {
              customerId: topicsInfo?.customer?.id,
              customerProductId: topicsInfo?.customerProduct?.id,
              imageFileName
            }
          });

          if (result.data?.getCustomerProductFileByCustomerProductId) {
            setUploadedImageFileContent(result.data.getCustomerProductFileByCustomerProductId.content ?? null);
          }
        } catch (e) {
          toast({
            title: t('common.label.failed'),
            description: t('features.Opportunity.TopicsModal.message.fetchProductImageFailed'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      }
      fetchData();

      // WebAPI.request({
      //   query: `
      //     query {
      //       getCustomerProductFileByCustomerProductId(customerId: "${customerId}", customerProductId: "${topicsInfo?.customerProduct?.id}", imageFileName: "${imageFileName}") {
      //         customerProductId
      //         customerId
      //         imageFileName
      //         content
      //       }
      //     }
      //   `
      // }).then(result => {
      //   if (result.data?.getCustomerProductFileByCustomerProductId) {
      //     setUploadedImageFileContent(result.data.getCustomerProductFileByCustomerProductId.content ?? null);
      //   }
      // }).catch(error => {
      //   toast({
      //     title: '失敗',
      //     description: `製品画像の取得に失敗しました。`,
      //     status: 'error',
      //     duration: 5000,
      //     isClosable: true,
      //   });
      //   throw new Error(error);
      // });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageFileName]);

  /**
   * 製品情報をWebAPIで取得する
   */
  const getCustomerProductInfo = async () => {
    try {
      const result = await WebAPI.request({
        query: `
          query getCustomerProductByCustomerProductId($customerProductId: String) {
            getCustomerProductByCustomerProductId(customerProductId: $customerProductId) {
              imageFileName
            }
          }`,
        variables: { customerProductId: topicsInfo?.customerProduct?.id }
      });
      if (!result.data || result.data.getCustomerProductByCustomerProductId === null) {
        throw result.errors;
      }
      setImageFileName(result.data?.getCustomerProductByCustomerProductId?.imageFileName);
    } catch (error) {
      toast({
        title: t('common.label.error'),
        description: t('features.Opportunity.TopicsModal.message.fetchCustomerProductFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw error;
    }
  };

  const getLatestCurrentStatus = (updates: string | null | undefined) => {
    if (!updates) return;
    try {
      const parsedUpdates: Update[] = JSON.parse(updates);
      if (Array.isArray(parsedUpdates)) {
        const sortedUpdates = parsedUpdates
          .map((update) => ({
            ...update,
            CurrentStatus: update.CurrentStatus || '',
          }))
          .sort((a, b) => new Date(b.UpdateDate).getTime() - new Date(a.UpdateDate).getTime());

        return setCurrentStatus({
          UpdateDate: sortedUpdates[0].UpdateDate,
          CurrentStatus: sortedUpdates[0].CurrentStatus,
        });
      }
      return;
    } catch (error) {
      console.error('Error parsing updates:', error);
      return;
    }
  };

  const modalBodyRef = React.useRef<HTMLDivElement>(null);

  // const handlePdfDownload = async () => {
  //   if (modalBodyRef.current) {
  //     const canvas = await html2canvas(modalBodyRef.current);
  //     const imgData = canvas.toDataURL('image/png');

  //     // 横向きのA4サイズのPDFを作成 (297mm x 210mm)
  //     const pdf = new jsPDF('l', 'mm', 'a4');
  //     const pdfWidth = pdf.internal.pageSize.getWidth();
  //     const pdfHeight = pdf.internal.pageSize.getHeight();
  //     // 画像の幅と高さを計算
  //     const imgWidth = canvas.width;
  //     const imgHeight = canvas.height;
  //     const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

  //     // 画像をPDFに追加
  //     pdf.addImage(imgData, 'PNG', 0, 0, imgWidth * ratio, imgHeight * ratio);

  //     pdf.save('download.pdf');
  //   }
  // };

  return (
    <>
      {topicsInfo && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          size="full"
        >
          <ModalOverlay />
          <ModalContent minW="1280px" minH="1024px" overflow="auto">
            {/* <ModalHeader>
              <Flex w="100%" justifyContent="space-between">
                <Button colorScheme="blue" onClick={handlePdfDownload}>PDF化</Button>
                <Button colorScheme="gray" onClick={onClose}>閉じる</Button>
              </Flex>
            </ModalHeader> */}

            {/* 戻るボタンに変更 */}
            <Box p={4}>
              <Button colorScheme="blue" onClick={onClose}>
                {t('common.button.back')}
              </Button>
            </Box>

            <ModalBody ref={modalBodyRef}>
              <Grid
                templateColumns="repeat(5, 1fr)"
                gap="8px"
              >
                <GridItem>
                  <Text
                    fontWeight="bold"
                    fontSize="18px"
                  >
                    {t('features.Opportunity.TopicsModal.label.customer')}
                  </Text>
                  <Text
                    fontWeight="bold"
                    fontSize="20px"
                  >
                    {topicsInfo.customer.name}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text
                    fontWeight="bold"
                    fontSize="18px"
                  >
                    {t('features.Opportunity.TopicsModal.label.endUser')}
                  </Text>
                  <Text
                    fontWeight="bold"
                    fontSize="20px"
                  >
                    {topicsInfo.endUser?.name}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text
                    fontWeight="bold"
                    fontSize="18px"
                  >
                    {t('features.Opportunity.TopicsModal.label.customerProduct')}
                  </Text>
                  <Text
                    fontWeight="bold"
                    fontSize="20px"
                  >
                    {topicsInfo.customerProduct?.name}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text
                    fontWeight="bold"
                    fontSize="18px"
                  >
                    {t('features.Opportunity.TopicsModal.label.mmiProductCategoryName')}
                  </Text>
                  <Text
                    fontWeight="bold"
                    fontSize="20px"
                  >
                    {topicsInfo.MMProductCategory?.name}
                  </Text>
                </GridItem>
                <GridItem>
                  <Flex justifyContent="end">
                    <Image src={logoImage} />
                  </Flex>
                </GridItem>
              </Grid>
              <Box
                borderBottom="2px solid"
                borderColor="gray.200"
                mt="16px"
              />
              <Grid
                templateColumns="repeat(4, 1fr)"
                templateRows="repeat(2, 1fr)"
                gap="12px"
                mt="32px"
              >
                <GridItem
                  position="relative"
                  h="100px"
                >
                  <TopicsItemBox
                    itemLabel={t('features.Opportunity.TopicsModal.label.customerProjectOpportunity')}
                    value={topicsInfo.opportunityName}
                  />
                </GridItem>
                <GridItem position="relative">
                  <TopicsItemBox
                    itemLabel={t('features.Opportunity.TopicsModal.label.partNumber')}
                    value={topicsInfo.partsNumber}
                  />
                </GridItem>
                <GridItem position="relative">
                  <TopicsItemBox
                    itemLabel={t('features.Opportunity.TopicsModal.label.mpDate')}
                    value={formatDate(topicsInfo.massProduction, YYYYMM_DISPLAY_FORMAT)}
                  />
                </GridItem>
                <GridItem rowSpan={2}>
                  <Box
                    w="100%"
                    h="calc(100% - 4px)"
                    maxH="220px"
                    p="8px"
                    border="1px solid #000000"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    {uploadedImageFileContent ? (
                      <Image
                        src={uploadedImageFileContent}
                        objectFit="contain"
                        w="100%"
                        h="100%"
                      />
                    ) : (
                      <Text
                        display="Flex"
                        alignItems="center"
                        justifyContent="center"
                        w="100%"
                        h="100%"
                        fontWeight="bold"
                      ></Text>
                    )}
                  </Box>
                </GridItem>
                <GridItem position="relative">
                  <TopicsItemBox
                    itemLabel={t('features.Opportunity.TopicsModal.label.application')}
                    value={topicsInfo.applicationCategory}
                  />
                </GridItem>
                <GridItem position="relative">
                  <TopicsItemBox
                    itemLabel={t('features.Opportunity.TopicsModal.label.salesTerritory')}
                    value={topicsInfo.MPLocationList?.map((item) => item.name).join(', ')}
                  />
                </GridItem>
                <GridItem position="relative">
                  <TopicsItemBox
                    itemLabel={t('features.Opportunity.TopicsModal.label.unitPerSet')}
                    value={topicsInfo.unitPerSet}
                  />
                </GridItem>
              </Grid>
              {/* ─── 左側：5つの数値ボックス ─── */}
              <Grid
                templateColumns="repeat(7, 1fr)"
                templateRows="repeat(4, auto)"
                gap="12px"
                mt="12px"
              >
                <GridItem rowStart={1} colStart={1} rowSpan={2} position="relative">
                  <TopicsItemBox
                    itemLabel={t('features.Opportunity.TopicsModal.label.price')}
                    headValue={topicsInfo.currency?.name}
                    value={topicsInfo.MMProductUnitPrice}
                  />
                </GridItem>
                <GridItem rowStart={1} colStart={2} rowSpan={2} position="relative">
                  <TopicsItemBox
                    itemLabel={t('features.Opportunity.TopicsModal.label.ttlQtyPerYear')}
                    value={topicsInfo.TTLQtyPerMonth ? topicsInfo.TTLQtyPerMonth * 12 : ''}
                  />
                </GridItem>
                <GridItem rowStart={1} colStart={3} rowSpan={2} position="relative">
                  <TopicsItemBox
                    itemLabel={t('features.Opportunity.TopicsModal.label.mmiQtyPerYear')}
                    value={topicsInfo.MMProductQuantityPerMonth ? topicsInfo.MMProductQuantityPerMonth * 12 : ''}
                  />
                </GridItem>
                <GridItem rowStart={1} colStart={4} rowSpan={2} position="relative">
                  <TopicsItemBox
                    itemLabel={t('features.Opportunity.TopicsModal.label.ttlAmtPerYear')}
                    headValue={topicsInfo.currency?.name}
                    value={
                      topicsInfo.MMProductUnitPrice && topicsInfo.TTLQtyPerMonth
                        ? topicsInfo.MMProductUnitPrice * topicsInfo.TTLQtyPerMonth * 12
                        : ''
                    }
                  />
                </GridItem>
                <GridItem rowStart={1} colStart={5} rowSpan={2} position="relative">
                  <TopicsItemBox
                    itemLabel={t('features.Opportunity.TopicsModal.label.mmiAmtPerYear')}
                    headValue={topicsInfo.currency?.name}
                    value={
                      topicsInfo.MMProductUnitPrice && topicsInfo.MMProductQuantityPerMonth
                        ? topicsInfo.MMProductUnitPrice * topicsInfo.MMProductQuantityPerMonth * 12
                        : ''
                    }
                  />
                </GridItem>

                {/* ─── 左下：Ramp Up Schedule ─── */}
                <GridItem rowStart={3} colStart={1} rowSpan={2} colSpan={5} textAlign="left" mt={-2}>
                  <Flex direction="column"
                    alignItems="flex-start"
                    h="100%">
                    <Text
                      fontWeight="bold"
                      fontSize="18px"
                    >
                      {t('features.Opportunity.TopicsModal.label.rampUpSchedule')}
                    </Text>

                    <Table h="calc(100% - 18px - 1px)" // タイトルの文字サイズ(18px)、調整(1px)を減算
                    >
                      <Thead>
                        <Tr
                          fontWeight="bold"
                          fontSize="20px"
                          h="40px"
                        >
                          <Th
                            border="1px solid #000000"
                            textAlign="center"
                            textColor="#000000"
                            fontSize="20px"
                          >
                            {formatDate(topicsInfo.schedule1, YYYYMM_DISPLAY_FORMAT)}
                          </Th>
                          <Th
                            border="1px solid #000000"
                            textAlign="center"
                            textColor="#000000"
                            fontSize="20px"
                          >
                            {formatDate(topicsInfo.schedule2, YYYYMM_DISPLAY_FORMAT)}
                          </Th>
                          <Th
                            border="1px solid #000000"
                            textAlign="center"
                            textColor="#000000"
                            fontSize="20px"
                          >
                            {formatDate(topicsInfo.schedule3, YYYYMM_DISPLAY_FORMAT)}
                          </Th>
                          <Th
                            border="1px solid #000000"
                            textAlign="center"
                            textColor="#000000"
                            fontSize="20px"
                          >
                            {formatDate(topicsInfo.schedule4, YYYYMM_DISPLAY_FORMAT)}
                          </Th>
                          <Th
                            border="1px solid #000000"
                            textAlign="center"
                            textColor="#000000"
                            fontSize="20px"
                          >
                            {formatDate(topicsInfo.schedule5, YYYYMM_DISPLAY_FORMAT)}
                          </Th>
                          <Th
                            border="1px solid #000000"
                            textAlign="center"
                            textColor="#000000"
                            fontSize="20px"
                          >
                            {formatDate(topicsInfo.schedule6, YYYYMM_DISPLAY_FORMAT)}
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr
                          fontWeight="bold"
                          fontSize="20px"
                          minH="90px"
                        >
                          <Td
                            border="1px solid #000000"
                            textAlign="center"
                          >
                            {topicsInfo.mileStone1}
                          </Td>
                          <Td
                            border="1px solid #000000"
                            textAlign="center"
                          >
                            {topicsInfo.mileStone2}
                          </Td>
                          <Td
                            border="1px solid #000000"
                            textAlign="center"
                          >
                            {topicsInfo.mileStone3}
                          </Td>
                          <Td
                            border="1px solid #000000"
                            textAlign="center"
                          >
                            {topicsInfo.mileStone4}
                          </Td>
                          <Td
                            border="1px solid #000000"
                            textAlign="center"
                          >
                            {topicsInfo.mileStone5}
                          </Td>
                          <Td
                            border="1px solid #000000"
                            textAlign="center"
                          >
                            {topicsInfo.mileStone6}
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </Flex>
                </GridItem>
                {/* ─── 右上：Competitor ─── */}
                <GridItem rowStart={1} colStart={6} colSpan={2} position="relative">
                  <TopicsItemBox
                    itemLabel={t('features.Opportunity.TopicsModal.label.competitor')}
                    minH="50px"
                    value={topicsInfo.competitorList?.map((c) => c.name).join(', ')}
                  />
                </GridItem>
                {/* ─── 右下：商流（Channel）テーブル ─── */}
                <GridItem rowStart={2} colStart={6} rowSpan={3} colSpan={2}>
                  <Table
                    h="100%"
                  >
                    <Thead>
                      <Tr h="40px">
                        <Th
                          border="1px solid #000000"
                          textAlign="center"
                          textColor="#000000"
                          fontSize="18px"
                          textTransform="none"
                        >
                          {t('features.Opportunity.TopicsModal.label.channel')}
                        </Th>
                        <Th
                          border="1px solid #000000"
                          textAlign="center"
                          textColor="#000000"
                          fontSize="18px"
                          textTransform="none"
                        >
                          {t('features.Opportunity.TopicsModal.label.type')}
                        </Th>
                        <Th
                          border="1px solid #000000"
                          textAlign="center"
                          textColor="#000000"
                          fontSize="18px"
                          textTransform="none"
                        >
                          {t('features.Opportunity.TopicsModal.label.company')}
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {CHANNEL_LEVELS.map((level) => {
                        const item = topicsInfo.channelList?.find((c) => c.channelLevel === level);
                        return (
                          <Tr key={level}>
                            <Td
                              border="1px solid #000000"
                              textAlign="center"
                              fontWeight="bold"
                              fontSize="20px"
                            >
                              {level}
                            </Td>
                            <Td
                              border="1px solid #000000"
                              textAlign="center"
                              fontWeight="bold"
                              fontSize="20px"
                            >
                              {item?.clientType ? CLIENT_TYPE_LABELS[item.clientType] : ''}
                            </Td>
                            <Td
                              border="1px solid #000000"
                              textAlign="center"
                              fontWeight="bold"
                              fontSize="20px"
                            >
                              {item?.clientName ?? ''}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </GridItem>
              </Grid>
              <Box mt="8px">
                <Flex>
                  <Text
                    fontWeight="bold"
                    fontSize="18px"
                  >
                    {t('features.Opportunity.TopicsModal.label.currentStatus')}
                  </Text>
                  <Text
                    ml="16px"
                    fontSize="18px"
                    color="#0000cd"
                  >
                    {t('features.Opportunity.TopicsModal.label.updateDate')}{formatDate(currentStatus?.UpdateDate, YYYYMMDD_DISPLAY_FORMAT)}
                  </Text>
                </Flex>
                <Box
                  h="195px"
                  p="8px"
                  border="1px solid #000000"
                >
                  {topicsInfo && topicsInfo.currentStatus && topicsInfo.currentStatus.length > 0 ? (
                    <Text
                      maxH="100%"
                      color="black"
                      whiteSpace="pre"
                      textAlign="left"
                      fontFamily="Meiryo UI"
                      fontSize="20px"
                      overflow="hidden"
                      textOverflow="clip"
                      display="block"
                    >
                      {currentStatus?.CurrentStatus}
                    </Text>
                  ) : (
                    <Text
                      fontSize="20px"
                      color="#0000cd"
                    >
                      {t('features.Opportunity.TopicsModal.message.noData')}
                    </Text>
                  )}
                </Box>
              </Box>
              <Table mt="7px">
                <Thead>
                  <Tr fontWeight="bold">
                    <Th
                      border="1px solid #FFFFFF"
                      textAlign="center"
                      textColor="#000000"
                      fontSize="18px"
                      textTransform="none"
                    >
                      {t('features.Opportunity.TopicsModal.label.opportunityStage')}
                    </Th>
                    <Th
                      border="1px solid #FFFFFF"
                      textAlign="center"
                      textColor="#000000"
                      fontSize="18px"
                      textTransform="none"
                    >
                      {t('features.Opportunity.TopicsModal.label.salesUnitName')}
                    </Th>
                    <Th
                      border="1px solid #FFFFFF"
                      textAlign="center"
                      bg="#1565C0"
                      textColor="#FFFFFF"
                      fontSize="18px"
                      textTransform="none"
                    >
                      {t('features.Opportunity.TopicsModal.label.salesPersonName')}
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr
                    fontWeight="bold"
                    fontSize="20px"
                  >
                    <Td
                      border="1px solid #FFFFFF"
                      textAlign="center"
                      bg="#FFCDD2"
                    >
                      {topicsInfo.opportunityStage?.name}
                    </Td>
                    <Td
                      border="1px solid #FFFFFF"
                      textAlign="center"
                      bg="#BBDEFB"
                    >
                      {topicsInfo?.salesUnit?.name}
                    </Td>
                    <Td
                      border="1px solid #FFFFFF"
                      textAlign="center"
                      bg="#BBDEFB"
                    >
                      {topicsInfo.salesPerson?.name}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </ModalBody>
          </ModalContent>
        </Modal >
      )}
    </>
  );
};

const TopicsItemBox: React.FC<ItemProps> = memo(({ itemLabel, value, minH = '100px' }) => (
  <Box
    position="relative"
    border="1px solid #000000"
    minH={minH}
    h="100%"
    w="100%"
    p="10px"
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <Box
      position="absolute"
      top={0}
      left={2}
      transform="translateY(-50%)"
      px={1}
      h={5} // itemLabelと同じ高さに絞る
      bg="#FFFFFF"
      fontWeight="bold"
      fontSize="18px"
      color="transparent" // itemLabelと同じサイズの白塗りを表示するだけのため、ここでは文字列を透明にする
      zIndex={1}
    >
      {itemLabel}
    </Box>
    <Text
      position="absolute"
      top={0}
      left={2}
      transform={'translateY(-50%)'}
      px={1}
      fontWeight="bold"
      fontSize="18px"
      zIndex={2}
    >
      {itemLabel}
    </Text>
    <Text
      whiteSpace="normal"
      wordBreak="break-word"
      fontWeight="bold"
      fontSize="20px"
      textAlign="left"
    >
      {typeof value === 'number' ? value.toLocaleString() : value}
    </Text>
  </Box>
));


export default TopicsModal;
